import type { AddressData } from '@choco-admin/types';
import { Button, Card, CardBody, CardHeader, Chip } from '@heroui/react';
import React, { useCallback, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

import {
  ChocoCheckbox,
  Input as FormInput,
  TextArea as FormTextArea,
  CountryFormField,
} from '../../../../components/FormComponents';
import { FormFooter } from '../../../../components/FormFooter';
import { AddressInput } from '../../../../components/GooglePlaces';

import { useBuyerForm } from './hooks/useBuyerForm';
import type { BuyerFormData, BuyerFormMode } from './types';

interface BuyerFormProps {
  /** Form mode (create or edit) */
  mode: BuyerFormMode;
  /** Initial form data for edit mode */
  initialData?: Partial<BuyerFormData>;
  /**
   * Form submission handler. Returns the values as they were persisted, which
   * are not always the values submitted: a google-to-custom switch is saved
   * with both place ids cleared. Returns nothing when the save failed.
   */
  onSubmit: (data: BuyerFormData) => Promise<BuyerFormData | undefined>;
  /** Cancel handler */
  onCancel?: () => void;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Additional form title */
  title?: string;
}

interface AddressSourceCue {
  title: string;
  message: string;
  color: 'default' | 'primary' | 'secondary';
}

/**
 * Which address picker the form is showing.
 *
 * `google` means the address comes from a Google business listing (an
 * establishment place), `custom` means it is picked address-by-address. This is
 * UI state, deliberately separate from the form's `addressSource`: pressing a
 * switch button changes what is on screen, and only an actual Places selection
 * changes the value that gets saved. That separation is what stops a half-made
 * switch from reaching the backend — `addressSource: 'google'` without a place id
 * and coordinates is a 400, and `custom` carrying the old place's coordinates is
 * the stale-address bug this form exists to fix.
 */
type AddressPickerMode = 'google' | 'custom';

/**
 * Applied to every address write. "Save Changes" is disabled while the edit form
 * is pristine, and a Places selection is made entirely of `setValue` calls — so
 * without this a picked address could not be saved at all (CRM-443). It also
 * keeps the flag honest in the other direction: react-hook-form recomputes
 * dirtiness against the loaded buyer, so restoring the stored address clears it.
 */
const MARK_DIRTY = { shouldDirty: true };

const getAddressSourceCue = ({
  addressSource,
  isLinkedToBusiness,
  isFromPlaces,
}: {
  addressSource?: BuyerFormData['addressSource'];
  /**
   * Whether a Google *business listing* still identifies this buyer, which is
   * `googlePlaceId` and that field alone. `googlePlaceAddressId` describes the
   * address, and a custom pick supplies one of its own, so reading both would
   * claim a business link for a buyer that has none.
   */
  isLinkedToBusiness: boolean;
  /**
   * Whether the address itself came from Places, which `googlePlaceAddressId`
   * answers. Most custom addresses are picked from the suggestions rather than
   * typed, so without this they would all be described as entered by hand.
   */
  isFromPlaces: boolean;
}): AddressSourceCue => {
  if (addressSource === 'google') {
    return {
      title: 'Google Places address',
      message: 'This address was selected from Google Places.',
      color: 'primary',
    };
  }

  if (addressSource === 'custom' && isLinkedToBusiness) {
    return {
      title: 'Custom address, linked to a Google business',
      message:
        'The address was picked separately from the business listing this buyer is still linked to.',
      color: 'secondary',
    };
  }

  if (addressSource === 'custom' && isFromPlaces) {
    return {
      title: 'Custom address',
      message: 'Picked from Google Places, not a business listing.',
      color: 'default',
    };
  }

  if (addressSource === 'custom') {
    return {
      title: 'Custom address',
      message: 'This address was entered by hand.',
      color: 'default',
    };
  }

  return {
    title: 'Address source unknown',
    message: 'Review the current address before saving changes.',
    color: 'default',
  };
};

export const BuyerForm: React.FC<BuyerFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  title,
}) => {
  const [showAllFormFields, setShowAllFormFields] = useState(mode === 'edit');
  const [businessNameInput, setBusinessNameInput] = useState('');
  const [addressSearchInput, setAddressSearchInput] = useState('');
  const [addressEditTarget, setAddressEditTarget] = useState<
    AddressPickerMode | undefined
  >(undefined);

  /**
   * The buyer name as it was before a business pick overwrote it, and the name
   * that pick applied.
   *
   * Picking a Google business fills the buyer name from the business, which is
   * what someone wants when the two are the same thing. Backing out with "Keep
   * current address" has to undo that as well, or the buyer keeps the other
   * business's name. `applied` is what tells an untouched name from one the
   * user has since typed over: only the untouched one is reverted.
   */
  const nameBeforeBusinessPick = useRef<{
    previous: string;
    applied: string;
  } | null>(null);

  // Read inside the async submit, which must see the current value rather than
  // the one captured when the callback was created.
  const addressEditTargetRef = useRef(addressEditTarget);
  addressEditTargetRef.current = addressEditTarget;

  /**
   * Hands the just-saved values back so the form can rebaseline on them, and
   * closes the address switch this save was made from. Returns undefined when
   * the save failed, which leaves the form dirty and the switch open.
   */
  const handleSubmitAndSettle = useCallback(
    async (data: BuyerFormData) => {
      const switchAtSubmit = addressEditTargetRef.current;
      const saved = await onSubmit(data);
      if (!saved) return undefined;

      // The controls stay live during a save, so someone can start a different
      // switch while it is in flight. Closing that one would shut the picker
      // they are working in and drop the address they just chose.
      if (addressEditTargetRef.current === switchAtSubmit) {
        setAddressEditTarget(undefined);
        setAddressSearchInput('');
        setBusinessNameInput('');
        nameBeforeBusinessPick.current = null;
      }

      return saved;
    },
    [onSubmit]
  );

  const form = useBuyerForm({
    mode,
    initialData,
    onSubmit: handleSubmitAndSettle,
  });

  // Address components are re-derived server-side from the stored Google place
  // on every save that carries an address (choco-backend updateBuyer.ts:72-77),
  // so hand-edits to a google-sourced buyer's address silently revert on save.
  // Lock the derived fields and require picking a new place to change them
  // (legacy-admin parity).
  const isGoogleSourced = form.watch('addressSource') === 'google';

  // The picker on screen: the target of a switch in progress, otherwise the
  // buyer's stored address source.
  const addressPickerMode: AddressPickerMode =
    addressEditTarget ?? (isGoogleSourced ? 'google' : 'custom');

  // A custom address only ever comes from a Places selection — on creation, so a
  // buyer can never be saved with an address Choco has no coordinates for
  // (CRM-360), and now on edit too, where free text left latitude, longitude,
  // time zone, city and country describing the previous address (CRM-444).
  const usesAddressSearch = addressPickerMode === 'custom';

  // Editing keeps the business search inside the address section, next to the
  // address it changes; on creation the search at the top of the form is the same
  // control and drives the rest of the form's disclosure.
  const usesBusinessSearch = mode === 'edit' && addressPickerMode === 'google';

  const selectedFullAddress = form.watch('fullAddress');

  const handleAddressChange = useCallback(
    (addressData: AddressData) => {
      // Show all form fields when address is selected
      setShowAllFormFields(true);

      // Update form with address data
      form.setValue('fullAddress', addressData.fullAddress, {
        ...MARK_DIRTY,
        shouldValidate: true,
      });
      form.setValue('addressSource', addressData.addressSource, MARK_DIRTY);
      // Remember what the pick overwrites, so backing out can put it back.
      // `previous` is held from the first pick: picking a second business must
      // not make the first business's name the thing we revert to.
      nameBeforeBusinessPick.current = {
        previous:
          nameBeforeBusinessPick.current?.previous ??
          (form.watch('name') || ''),
        applied: addressData.name || '',
      };
      form.setValue('name', addressData.name, MARK_DIRTY);
      form.setValue('addressCity', addressData.addressCity, MARK_DIRTY);
      form.setValue('addressCountry', addressData.addressCountry, MARK_DIRTY);
      form.setValue('addressLatitude', addressData.addressLatitude, MARK_DIRTY);
      form.setValue(
        'addressLongitude',
        addressData.addressLongitude,
        MARK_DIRTY
      );
      form.setValue(
        'addressUTCOffset',
        addressData.addressUTCOffset,
        MARK_DIRTY
      );
      form.setValue(
        'addressGooglePlaceId',
        addressData.addressGooglePlaceId,
        MARK_DIRTY
      );
      form.setValue(
        'addressGoogleAddressId',
        addressData.addressGoogleAddressId,
        MARK_DIRTY
      );

      // Clear the business name input since the name goes to the actual name field
      setBusinessNameInput('');
    },
    [form]
  );

  /**
   * A Places pick for the address only. Unlike the business search this must not
   * touch the buyer name — for an `address` result Google returns the street as
   * `name`, which would overwrite what the user typed.
   */
  const handleCustomAddressSelect = useCallback(
    (addressData: AddressData) => {
      form.setValue('fullAddress', addressData.fullAddress, {
        ...MARK_DIRTY,
        shouldValidate: true,
      });
      form.setValue('addressSource', 'custom', MARK_DIRTY);
      form.setValue('addressCity', addressData.addressCity, MARK_DIRTY);
      form.setValue('addressCountry', addressData.addressCountry, {
        ...MARK_DIRTY,
        shouldValidate: true,
      });
      form.setValue('addressLatitude', addressData.addressLatitude, {
        ...MARK_DIRTY,
        shouldValidate: true,
      });
      form.setValue('addressLongitude', addressData.addressLongitude, {
        ...MARK_DIRTY,
      });
      form.setValue('addressUTCOffset', addressData.addressUTCOffset, {
        ...MARK_DIRTY,
      });
      form.setValue(
        'addressGoogleAddressId',
        addressData.addressGoogleAddressId,
        MARK_DIRTY
      );
      setAddressSearchInput(addressData.fullAddress);
    },
    [form]
  );

  /**
   * Typing means the user is looking for a different address, so drop the
   * previous selection — keeping its coordinates would pair them with an
   * address the user never picked.
   */
  const handleCustomAddressTyping = useCallback(
    (value: string) => {
      setAddressSearchInput(value);
      if (!form.watch('fullAddress')) return;
      form.setValue('fullAddress', '', {
        ...MARK_DIRTY,
        shouldValidate: true,
      });
      form.setValue('addressCity', '', MARK_DIRTY);
      form.setValue('addressCountry', '', MARK_DIRTY);
      form.setValue('addressLatitude', undefined, MARK_DIRTY);
      form.setValue('addressLongitude', undefined, MARK_DIRTY);
      form.setValue('addressUTCOffset', undefined, MARK_DIRTY);
      form.setValue('addressGoogleAddressId', undefined, MARK_DIRTY);
    },
    [form]
  );

  /**
   * Puts every address field back to the loaded buyer's values.
   *
   * Every switch starts here, so the form only ever holds one of two things: the
   * stored address exactly as it is, or one complete Places selection. Without
   * that, switching twice could leave a half-made address — source flipped, place
   * id gone, previous street text kept — which the backend either rejects or
   * stores as a buyer whose coordinates belong to somewhere else.
   */
  const restoreStoredAddress = useCallback(() => {
    form.setValue('fullAddress', initialData?.fullAddress ?? '', {
      ...MARK_DIRTY,
      shouldValidate: true,
    });
    form.setValue('addressSource', initialData?.addressSource, MARK_DIRTY);
    form.setValue('addressCity', initialData?.addressCity ?? '', MARK_DIRTY);
    form.setValue(
      'addressCountry',
      initialData?.addressCountry ?? '',
      MARK_DIRTY
    );
    form.setValue('addressLatitude', initialData?.addressLatitude, MARK_DIRTY);
    form.setValue(
      'addressLongitude',
      initialData?.addressLongitude,
      MARK_DIRTY
    );
    form.setValue(
      'addressUTCOffset',
      initialData?.addressUTCOffset,
      MARK_DIRTY
    );
    form.setValue(
      'addressGooglePlaceId',
      initialData?.addressGooglePlaceId,
      MARK_DIRTY
    );
    form.setValue(
      'addressGoogleAddressId',
      initialData?.addressGoogleAddressId,
      MARK_DIRTY
    );
    // The business search writes the buyer name too, so the restore has to
    // cover it. A name the user typed after the pick is theirs and is kept.
    const overwritten = nameBeforeBusinessPick.current;
    if (overwritten && (form.watch('name') || '') === overwritten.applied) {
      form.setValue('name', overwritten.previous, MARK_DIRTY);
    }
    nameBeforeBusinessPick.current = null;

    setAddressSearchInput('');
    setBusinessNameInput('');
  }, [form, initialData]);

  /**
   * Moves the form onto picking a plain address. Everything the previous place
   * provided goes: the address text, its coordinates, its time zone, its city and
   * country. `fullAddress` is required, so the form cannot be saved again until a
   * Places pick has refilled all of them together.
   *
   * The place ids are cleared here only so the panel shows what the save will do
   * — sending the explicit nulls that actually clear them is the payload
   * builder's job (`buildBuyerUpdateInput`).
   */
  const handleUseCustomAddress = useCallback(() => {
    restoreStoredAddress();
    setAddressEditTarget('custom');
    form.setValue('fullAddress', '', { ...MARK_DIRTY, shouldValidate: true });
    form.setValue('addressCity', '', MARK_DIRTY);
    form.setValue('addressCountry', '', MARK_DIRTY);
    form.setValue('addressLatitude', undefined, MARK_DIRTY);
    form.setValue('addressLongitude', undefined, MARK_DIRTY);
    form.setValue('addressUTCOffset', undefined, MARK_DIRTY);
    form.setValue('addressGooglePlaceId', undefined, MARK_DIRTY);
    form.setValue('addressGoogleAddressId', undefined, MARK_DIRTY);
  }, [form, restoreStoredAddress]);

  /**
   * Moves the form onto picking a Google business. The stored address stays on
   * screen and stays saved until a business is picked — a save made in between
   * carries no address at all, which is the only safe payload here: the backend
   * rejects a Google address that has no place id and coordinates.
   */
  const handleUseGoogleAddress = useCallback(() => {
    restoreStoredAddress();
    setAddressEditTarget('google');
  }, [restoreStoredAddress]);

  const handleKeepStoredAddress = useCallback(() => {
    restoreStoredAddress();
    setAddressEditTarget(undefined);
  }, [restoreStoredAddress]);

  const handleCreateCustomBuyer = useCallback(() => {
    setShowAllFormFields(true);
    // Seed the custom buyer's name from the search text — this is an explicit
    // action, unlike typing (#106).
    handleAddressChange({
      addressSource: 'custom',
      fullAddress: '',
      name: businessNameInput.trim(),
    });
  }, [handleAddressChange, businessNameInput]);

  const handleClearForm = useCallback(() => {
    form.reset();
    setShowAllFormFields(false);
    setBusinessNameInput('');
    setAddressSearchInput('');
  }, [form]);

  const addressSourceWasProvided = Boolean(
    initialData && 'addressSource' in initialData
  );
  const addressSource = addressSourceWasProvided
    ? form.watch('addressSource')
    : undefined;
  const isLinkedToBusiness = Boolean(form.watch('addressGooglePlaceId'));
  const isFromPlaces = Boolean(form.watch('addressGoogleAddressId'));
  const addressSourceCue = getAddressSourceCue({
    addressSource,
    isLinkedToBusiness,
    isFromPlaces,
  });

  // The coordinate requirement has no field of its own, so surface it with the
  // address search — otherwise a blocked submit would look like a dead button.
  const addressSelectionError =
    form.errors.fullAddress?.message ??
    form.errors.addressLatitude?.message ??
    form.errors.addressCountry?.message;

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* HeroUI cards clip their content; the Places dropdown is absolutely
          positioned and hangs below the collapsed card, so it needs to escape. */}
      <Card className="overflow-visible">
        {title && (
          <CardHeader>
            <h2 className="text-xl font-semibold">{title}</h2>
          </CardHeader>
        )}
        <CardBody className="overflow-visible">
          <form
            className="flex flex-col w-full space-y-8"
            onSubmit={form.handleSubmit}
          >
            {/* Creation starts from the business search: it is what reveals the
                rest of the form. When editing, the same search lives in the
                address section instead, next to the address it changes. */}
            {mode === 'create' && (
              <div className="space-y-2">
                <AddressInput
                  id="business-search"
                  label="Business Name (Search Google Places)"
                  isRequired={true}
                  placeholder="Search for business location..."
                  value={businessNameInput}
                  onAddressChange={handleAddressChange}
                  onManualInput={(value: string) => {
                    // Only track the search text locally — the Buyer Name field
                    // must not be overwritten until a place is actually
                    // selected from the predictions (#106). Typing must not
                    // reveal the rest of the form either: that was the way into
                    // saving a hand-typed, uncoordinated address (CRM-360).
                    setBusinessNameInput(value);
                  }}
                  isCustom={false}
                  onCreateCustom={handleCreateCustomBuyer}
                  showCustomOption={true}
                />
              </div>
            )}

            {/* Rest of form - Only visible after address selection or in edit mode */}
            {showAllFormFields && (
              <>
                {/* Basic Information */}
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-foreground border-b border-divider pb-2">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Controller
                      name="name"
                      control={form.control}
                      render={({ field }) => (
                        <FormInput
                          id="buyer-name"
                          name={field.name}
                          label="Buyer Name"
                          value={field.value}
                          placeholder="Enter buyer name"
                          required={true}
                          isInvalid={!!form.errors.name}
                          errorMessage={form.errors.name?.message}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Controller
                      name="displayName"
                      control={form.control}
                      render={({ field }) => (
                        <FormInput
                          id="buyer-display-name"
                          name={field.name}
                          label="Display Name"
                          value={field.value || ''}
                          placeholder="Enter display name"
                          isInvalid={!!form.errors.displayName}
                          errorMessage={form.errors.displayName?.message}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-foreground border-b border-divider pb-2">
                    Address Information
                  </h3>
                  {mode === 'edit' && (
                    <div className="space-y-3 rounded-lg border border-default-200 bg-default-50 px-4 py-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1">
                          <p className="text-xs font-medium uppercase text-default-500">
                            Address source
                          </p>
                          <p className="text-sm text-default-700">
                            {addressSourceCue.message}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 sm:items-end">
                          <Chip
                            color={addressSourceCue.color}
                            size="sm"
                            variant="flat"
                            className="w-fit"
                          >
                            {addressSourceCue.title}
                          </Chip>
                          <div className="flex flex-wrap gap-2">
                            {addressPickerMode === 'google' ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="light"
                                onPress={handleUseCustomAddress}
                                data-testid="use-custom-address"
                              >
                                Use a custom address
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="light"
                                onPress={handleUseGoogleAddress}
                                data-testid="use-google-address"
                              >
                                Use a Google business address
                              </Button>
                            )}
                            {addressEditTarget && (
                              <Button
                                type="button"
                                size="sm"
                                variant="bordered"
                                onPress={handleKeepStoredAddress}
                                data-testid="keep-stored-address"
                              >
                                Keep current address
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      {addressEditTarget && (
                        <p
                          className="text-sm text-default-700"
                          data-testid="address-switch-note"
                        >
                          {addressEditTarget === 'custom'
                            ? 'Pick the new address from the Google suggestions. This buyer will no longer be linked to a Google business listing.'
                            : 'Pick the business from the Google suggestions. Its name and its address both replace what is stored.'}
                        </p>
                      )}
                    </div>
                  )}
                  {usesBusinessSearch && (
                    <AddressInput
                      id="buyer-business-search"
                      label="Google business"
                      placeholder="Search for the business..."
                      value={businessNameInput}
                      onAddressChange={handleAddressChange}
                      onManualInput={setBusinessNameInput}
                      isCustom={false}
                      // Editing an existing buyer is no place to offer creating a
                      // custom one — here the search exists only to re-pick the
                      // Google business for this buyer.
                      showCustomOption={false}
                    />
                  )}
                  <div className="space-y-4">
                    {usesAddressSearch ? (
                      <div className="space-y-2">
                        <AddressInput
                          id="buyer-full-address-search"
                          label="Address"
                          isRequired={true}
                          placeholder="Search for an address..."
                          value={addressSearchInput}
                          onAddressChange={handleCustomAddressSelect}
                          onManualInput={handleCustomAddressTyping}
                          isCustom={true}
                          showCustomOption={false}
                        />
                        {selectedFullAddress ? (
                          <div
                            className="rounded-lg border border-default-200 bg-default-50 px-3 py-2"
                            data-testid="selected-custom-address"
                          >
                            <p className="text-xs font-medium uppercase text-default-500">
                              {mode === 'edit'
                                ? 'Current address'
                                : 'Selected address'}
                            </p>
                            <p className="text-sm text-foreground">
                              {selectedFullAddress}
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-default-500">
                            Pick an address from the suggestions — Choco stores
                            its coordinates and time zone for delivery and order
                            routing.
                          </p>
                        )}
                        {addressSelectionError && (
                          <p
                            className="text-xs text-danger"
                            data-testid="custom-address-error"
                          >
                            {addressSelectionError}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Controller
                          name="fullAddress"
                          control={form.control}
                          render={({ field }) => (
                            <FormTextArea
                              id="buyer-full-address"
                              name={field.name}
                              label="Full Address"
                              value={field.value || ''}
                              placeholder="Enter full address"
                              rows={3}
                              // This branch only renders while the address comes
                              // from a Google business, so it is never typed:
                              // the backend re-derives the components from the
                              // place on every save and would revert the edit.
                              disabled={true}
                              isInvalid={!!form.errors.fullAddress}
                              errorMessage={form.errors.fullAddress?.message}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    )}
                    {/* Next to the fields it describes. Above the address it read
                        as a note about the address itself, which is the one thing
                        on this card it is not about. */}
                    <p
                      className="text-xs text-default-500"
                      data-testid="address-derived-note"
                    >
                      {usesAddressSearch
                        ? 'City and country come from the address above.'
                        : 'City and country come from the selected business.'}
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Controller
                        name="addressCity"
                        control={form.control}
                        render={({ field }) => (
                          <FormInput
                            id="buyer-city"
                            name={field.name}
                            label="City"
                            value={field.value || ''}
                            placeholder="Enter city"
                            // City and country always come from the Places
                            // selection now: they are two of the five fields a
                            // hand-edit used to leave describing the previous
                            // address (CRM-444).
                            disabled={true}
                            isInvalid={!!form.errors.addressCity}
                            errorMessage={form.errors.addressCity?.message}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <CountryFormField
                        name="addressCountry"
                        control={form.control}
                        label="Country"
                        isRequired={false}
                        isDisabled={true}
                      />
                    </div>
                  </div>
                </div>

                {/* No heading. In edit mode this is one permanently disabled
                    checkbox, and a section header for it reads as a group that
                    lost its contents. */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    <Controller
                      name="isTest"
                      control={form.control}
                      render={({ field }) => (
                        <ChocoCheckbox
                          isSelected={field.value}
                          onValueChange={field.onChange}
                          isDisabled={mode === 'edit'}
                          size="sm"
                          label="Test Buyer"
                          description={
                            mode === 'edit'
                              ? 'Cannot be changed after creation'
                              : 'Mark this buyer as a test account'
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Global form error display */}
                {form.errors.root && (
                  <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
                    <span className="text-danger text-small">
                      {form.errors.root.message}
                    </span>
                  </div>
                )}

                {/* Form Actions */}
                <div className="pt-6 border-t border-divider">
                  <div className="flex justify-between">
                    <div>
                      {mode === 'create' && (
                        <Button
                          type="button"
                          variant="bordered"
                          onPress={handleClearForm}
                          isDisabled={form.isSubmitting}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <FormFooter
                      onSave={form.handleSubmit}
                      onCancel={onCancel || (() => {})}
                      saveText={
                        mode === 'create' ? 'Create Buyer' : 'Save Changes'
                      }
                      cancelText="Cancel"
                      isSaveLoading={form.isSubmitting}
                      isSaveDisabled={mode === 'edit' && !form.isDirty}
                      isCancelDisabled={form.isSubmitting}
                    />
                  </div>
                </div>
              </>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  );
};
